import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
    clearFinancialRequestsSearch,
    loadParticipantsAdvancedSearch,
    searchFinancialRequests
} from '../../../store/participantService';
import { calcStartDate } from '../../../utils/dateFunctions';
import { FINANCIAL_REQUESTS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../utils/sortFunctions';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import FinancialRequestsTableRow from './FinancialRequestsTableRow';

import app from '../../../app.module.css';

const headCells = [
    { id: 'ptCode', label: 'PT ID' },
    { id: 'participantName', label: 'PT Name' },
    { id: 'adviserName', label: 'Adviser' },
    { id: 'requestedByName', label: 'Requester' },
    { id: 'teamName', label: 'Team' },
    { id: 'amount', label: 'Amount' },
    { id: 'status', label: 'Status' },
    { id: 'dateIssued', label: 'Payment Date' },
    { id: 'dateRequested', label: 'Created Date' },
    { id: 'paymentMethodType', label: 'Payment Method Type' },
    { id: 'authorisedByName', label: 'Authorised By' },
    { id: 'requestType', label: 'Expenditure Type' },
    { id: 'paymentReason', label: 'Reason' },
    { id: 'evidenceDocument', label: 'Document Attached' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'dateRequested',
    page: 0,
    rowsPerPage: 100
};

const FinancialRequests = () => {
    // HOOKS
    const dispatch = useDispatch();
    const { state } = useLocation();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [participantIdsForRows, setParticipantIdsForRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [selectedOptions, setSelectedOptions] = useState({});

    // STORE STATE
    const teamsForService = useSelector((state) => state.entities.userService.teamsForService);
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const participants = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const participantsSearchMetaData = useSelector(
        (state) => state.entities.participantService.participantsSearchMetaData
    );
    const loadingParticipants = useSelector(
        (state) => state.entities.participantService.loadingParticipantsSearch
    );
    const financialRequests = useSelector(
        (state) => state.entities.participantService.financialRequestsSearch
    );
    const financialRequestsMetaData = useSelector(
        (state) => state.entities.participantService.financialRequestsSearchMetaData
    );
    const financialRequestsLoading = useSelector(
        (state) => state.entities.participantService.loadingFinancialRequestsSearch
    );

    // USE EFFECTS
    useEffect(() => {
        if (!state) return;
        reset();
        const options = {
            contractIds: [state.selects.contract.id],
            serviceIds: [state.selects.serviceId],
            teamIds: state.selects.teamIds,
            dateRequestedFrom: calcStartDate(4, state.selects.date),
            dateRequestedTo: state.selects.date
        };
        dispatch(searchFinancialRequests(options));
        setSelectedOptions(options);
    }, [state]);

    useEffect(() => {
        if (financialRequests?.length < 1) {
            setRows([]);
            setRowMetaData(initialRowMetaData);
            return;
        }
        const newParticipantIdsForRows = [
            ...new Set(financialRequests.map((el) => el.participantId))
        ];
        if (newParticipantIdsForRows.length > 0) {
            setParticipantIdsForRows(newParticipantIdsForRows);
            dispatch(loadParticipantsAdvancedSearch({ ids: newParticipantIdsForRows }));
        }
    }, [financialRequests]);

    useEffect(() => {
        if (participants?.length > 0 && !participantsSearchMetaData.last) {
            dispatch(
                loadParticipantsAdvancedSearch(
                    { ids: participantIdsForRows },
                    participantsSearchMetaData.number + 1
                )
            );
        }
    }, [participants, participantsSearchMetaData.last]);

    useEffect(() => {
        if (
            !(participants.length > 0) ||
            loadingParticipants ||
            financialRequestsLoading ||
            (participantIdsForRows.length > 0 && !participantsSearchMetaData.last)
        )
            return;
        const newRows = financialRequests.map((el) => {
            const participant = participants.find((entry) => entry.id === el.participantId);
            const adviser = users.find((entry) => entry.id === participant.userId);
            const team = teamsForService.find((entry) => entry.id === participant.teamId);
            const requestedBy = users.find((entry) => entry.id === el.adviserId);
            const authorisedBy = users.find((entry) => entry.id === el.approverId);
            return {
                ...el,
                dateIssued: el.dateIssued || '1960-01-01',
                dateRequested: el.dateRequested,
                paymentMethodType: el.paymentMethodType ?? '',
                paymentReason: el.paymentReason ?? '',
                evidenceDocument: el.evidenceDocumentId ? 'Yes' : 'No',
                ptCode: participant?.ptCode,
                participantName: participant
                    ? `${participant?.firstName} ${participant?.lastName}`
                    : '',
                adviserName: adviser ? `${adviser?.firstName} ${adviser?.lastName}` : '',
                requestedByName: requestedBy
                    ? `${requestedBy?.firstName} ${requestedBy?.lastName}`
                    : '',
                teamName: team?.name || '',
                authorisedByName: authorisedBy
                    ? `${authorisedBy?.firstName} ${authorisedBy?.lastName}`
                    : ''
            };
        });
        setRows(newRows);
    }, [
        financialRequests,
        financialRequestsLoading,
        loadingParticipants,
        participantIdsForRows,
        participantsSearchMetaData.last
    ]);

    // HELPER FNS
    const loadingError = () => {
        if (!state) return 'No Data Set query found for Financial Requests';
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <FinancialRequestsTableRow key={el.id} row={el} />
        ));

    const reset = () => {
        dispatch(clearFinancialRequestsSearch());
        setRows([]);
        setRowMetaData(initialRowMetaData);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={app.pageContainer}>
            <h1 className={app.mainHeading}>Data Sets Results Screen - Financial Requests</h1>
            {rows?.length < 1 ? (
                <LoadingSpinner content={FINANCIAL_REQUESTS_ERROR} />
            ) : (
                <ResultsTable
                    defaultOrderBy={'dateRequested'}
                    headCells={headCells}
                    loadingValues={financialRequestsLoading || loadingParticipants}
                    loadResults={searchFinancialRequests}
                    passRowMetaDataUp={setRowMetaData}
                    apiParams={selectedOptions}
                    tableRows={createRows()}
                    totalResults={financialRequestsMetaData.totalElements}
                    metaData={financialRequestsMetaData}
                />
            )}
        </div>
    );
};

export default FinancialRequests;
